import React, { useRef, useState } from "react";
import { isMobile } from "react-device-detect";

import { IconButton, Grid } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';

import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import AssignmentIcon from "@mui/icons-material/Assignment";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import InventoryIcon from '@mui/icons-material/Inventory';
import EventIcon from '@mui/icons-material/Event';
import LinkIcon from '@mui/icons-material/Link';
import XIcon from '@mui/icons-material/Close';
import TryIcon from '@mui/icons-material/Try';
import ComJabuti from "../../../img/michaelangelo_jabuti_vermelho.png";
import PropImiIcon from "../../../img/proposicao_imi.png";
import styled from 'styled-components';

import { useAlert } from "../../../contexts/AlertContext";
import { usePallet } from '../../../contexts/PalletContext';

import NotificationsContextService, { INotificationsContextResultData } from '../../../services/NotificationsContextService';
import ProposicaoService from "../../../services/ProposicaoService";
import TabelasService from "../../../services/TabelasService";

import { formatDate } from '../../../utils/dateUtils';

import NotificacaoMenuButton from './NotificacaoMenuButton';

import ButtonSeguir from "../../../components/ButtonSeguir";
import ButtonNaoSeguir from "../../../components/ButtonNaoSeguir";
import FavoritosService from "../../../services/FavoritosService";
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import maqui_head from './../../../img/maqui_head.png';
import clsx from "clsx";
import useStyles from "../../../components/layout/Styles";
import { Paper, Typography, Tooltip } from '@mui/material';
import { useAuthorization } from "../../../contexts/AuthorizationContext";
import CitacaoModal from "./CitacaoModal";

// ===================== Styles =====================
const NotificacaoNotContent = styled(Grid)`
  padding: 14px;
  a {
    text-decoration: none;
    color: #4B1196;
  }
  span { color: #4B1196; font-weight: 600; }
  b { color: #1E0736 }
  ul {
    padding-left: 0px;
    li {
      color: #1E0736;
      font-weight: 400;
      word-break: break-word;
      list-style-type: '';
      ::before{ content: '»'; }
      ul {
        li { ::before{ content: '⋙'; } padding-left: 1em; }
      }
    }
  }
`;

const NotificacaoNotFooter = styled(Grid)`
  padding: 0 14px 14px 14px;
  a { color: #1E0736; display: flex; text-decoration: unset; }
  svg { color: #1E0736; margin-right: 10px }
`;

// ===================== Palette =====================
const PALETTE = {
    gray: '#E8E8E8',
    purple: '#4B1196',
    lilac: '#dbcfea',
    bg: '#F3F5F7',
};

// ===================== Vote widget =====================
function SeuMercadoWidget({
    destaqueText,
    isInteresse,
}: {
    destaqueText?: any;
    isInteresse: boolean;
}) {
    if (!isInteresse) return null;
    return (
        <Tooltip title={<>{destaqueText}</>} arrow>
            <Paper
                elevation={0}
                sx={{
                    ml: 1,
                    mr: 1,
                    px: 1.25,
                    py: 0.75,
                    border: `2px solid ${PALETTE.purple}`,
                    bgcolor: '#4de800',
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'default',
                }}
            >
                <Typography
                    variant="body2"
                    style={{ fontWeight: 600 }}
                    sx={{
                        textTransform: 'uppercase',
                        color: PALETTE.purple,
                        whiteSpace: 'nowrap',
                    }}
                >
                    Relevante
                </Typography>
            </Paper>
        </Tooltip>
    );
}

const getIcon = (modulo: string, cor: string) => {
    switch (modulo) {
        case 'PROPOSIÇÃO':
            return <AssignmentIcon style={{ color: cor }} />
        case 'INTERLOCUTOR':
            return <AccountBoxIcon style={{ color: cor }} />
        case 'AGENDA':
            return <EventIcon style={{ color: cor }} />
        case 'CITAÇÕES':
            return <TryIcon style={{ color: cor }} />
        case 'TWITTER':
            return <XIcon style={{ color: cor }} />
    };
};

export interface IDeleted { deletado: boolean };

// ===================== Card =====================
const NotificacaoCard = (props: {
    notification?: INotificationsContextResultData,
    central?: boolean,
    checked?: boolean,
    onDelete?: (event: any) => void,
    onChange?: (event: any) => void,
    limpar?: boolean,
    index?: number,
    arquivado?: boolean,
    status?: string,
    reloadList?: () => void,
    idsFlow?: (event: any, ids: number) => void,
    arquivou?: string,
    idPermissao?: number,
    filtro?: string
    removeNotificacao?: (id: number) => void
    incrementaContador?: (incremento: number) => void
    countPendencia?: () => void,
}) => {
    const classes = useStyles();
    const { pallet } = usePallet();
    const { NewAlert } = useAlert();
    const [checked, setChecked] = React.useState(false);
    const [oculta, setOculta] = React.useState(false);
    const [favoritado, setFavoritado] = React.useState<boolean>(false);
    const [idFavorito, setIdFavorito] = React.useState(0);
    const { getConfig } = useAuthorization();

    const handleChange = (event?: any, index?: any, id?: number) => {
        props.idsFlow && props.idsFlow(event, id!);
        setChecked(event);
    };

    React.useEffect(() => {
        if (props.limpar === true) {
            setChecked(false);
        }
    }, [props.checked, props.limpar]);

    const handleArquivar = () => {
        setOculta(true);
        props.incrementaContador && props.incrementaContador(-1);

        NotificationsContextService.Arquivar(props.notification!.id).then((res) => {
            if (res) {
                NewAlert("success", "Notificação arquivada.");
                props?.removeNotificacao && props?.removeNotificacao(props.notification!.id);
                setOculta(false);
                if (props.central) {
                    props?.reloadList && props?.reloadList();
                }
            } else {
                setOculta(false);
                props?.incrementaContador && props?.incrementaContador(1);
                NewAlert("error", "Houve um erro. Tente novamente.");
            }
        });
        props?.countPendencia?.();
    };

    const handleDesarquivar = () => {
        NotificationsContextService.Desarquivar(props.notification!.id).then((res) => {
            if (res) {
                NewAlert("success", "Notificação desarquivada.");
                props.reloadList && props.reloadList();
            } else {
                NewAlert("error", "Houve um erro. Tente novamente.");
            }
        });
    };

    const checandoProp = (id: number) => {
        TabelasService.getProposicaoDeleted(Number(id)).then(proposicao => {
            if (proposicao !== undefined) {
                if (proposicao[0]?.deletado) {
                    NewAlert("error", "Proposição excluida")
                } else {
                    window.open(props.notification?.path, "_blank")
                }
            } else {
                window.open(props.notification?.path, "_blank")
            }
        });
    };

    const tratarId = (caminho: string) => {
        return caminho.substring(caminho.lastIndexOf('/') + 1, caminho.length);
    };

    const permissaoErro = () => {
        NewAlert("error", "Não é possível realizar está ação com este perfil.");
    };

    const seguirClick = (id: number, value: boolean, path: string = undefined as any) => {
        ProposicaoService.SetSeguir(id, value, path).then(
            res => {
                if (res) {
                    NewAlert('success', 'Seguiu com sucesso');
                    handleArquivar();
                } else {
                    NewAlert('error', 'Ocorreu um erro');
                }
            }
        );
    };

    const getCorTitleBackground = (tipo: number) => {
        switch (tipo) {
            case 100: return '#C0C0C0';
            case 103: return '#1E90FF';
            case 112: return '#7FFFD4';
            case 200: return '#DAA520';
            case 202: return '#F5DEB3';
            case 300: return '#90EE90';
            case 406: return '#F08080';
            default: return '#DED3FF';
        }
    };

    const getCorIcon = (tipo: number) => {
        switch (tipo) {
            case 100: return '#808080';
            case 103: return '#191970';
            case 112: return '#008080';
            case 200: return '#B8860B';
            case 202: return '#DEB887';
            case 300: return '#006400';
            case 406: return '#800000';
            default: return '#4B1196';
        }
    };

    React.useEffect(() => {
        if (props.notification?.tipo === 100) {
            if (props.notification.favoritado === 'sim') {
                setIdFavorito(props.notification.id)
                setFavoritado(true)
            } else {
                setFavoritado(false)
            }
        }
    }, [props.notification]);

    const handleChangeFavorito = async (event: any, id: number, modulo: string,) => {
        const data = { modulo, id_modulo: id, tipo: 'PROPOSIÇÕES' };

        FavoritosService.getFavoritoUnico(props.notification!.referencia, 'proposicao').then((res) => {
            if (res.id) {
                setFavoritado(false)
                FavoritosService.Delete(id, res.id, false, 'PROPOSIÇÕES').then((res) => {
                    if (res) {
                        NewAlert("success", "O item foi removido dos favoritos com sucesso.");
                        setFavoritado(false)
                    } else {
                        NewAlert("error", "Erro ao Deletar, tente novamente.");
                    }
                });
            } else {
                FavoritosService.Create(data).then((res) => {
                    if (res) {
                        NewAlert("success", "O item foi adicionado aos favoritos com sucesso.");
                    } else {
                        NewAlert("error", "Erro ao favoritar, verifique os campos e salve novamente.");
                    }
                });
            }
        });
    }

    const mercadoNomesRetorno = (arrayNomes) => {
        if (arrayNomes && arrayNomes.length > 0) {
            arrayNomes.map((x) => {
                return (
                    <div>
                        x
                    </div>
                )
            })
        }
    }


    const [excedeu, setExcedeu] = useState(false);
  const ref = useRef<T>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  React.useEffect(() => {
    const element = ref.current;

    if (!element) return;

    const checkOverflow = () => {
      setIsOverflowing(
        element.scrollHeight > element.clientHeight
      );
    };

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);

    observer.observe(element);

    return () => observer.disconnect();
  }, []);
    // console.log(props?.notification)

    return (
        <Grid key={props.notification?.id}
            className={clsx({ [classes.hidden]: oculta })}
            style={{ alignItems: isMobile ? undefined : 'center', border: '1px solid #EAEAEA', margin: '10px 0' }}
        >
            <div style={{
                display: 'flex', flexWrap: 'nowrap', flexDirection: 'row', width: '100%',
                height: isMobile ? '170px' : '100%', borderBottom: '1px solid #EAEAEA', backgroundColor: '#F9F9F9',
            }}>

                <div style={{
                    backgroundColor: props?.notification?.tipo === 119 ? '#DEA9BE' : getCorTitleBackground(props.notification!.tipo),
                    width: '50px', height: '58px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                        {props?.notification?.tipo === 119 ? (
                            <img src={ComJabuti} alt="Icone" style={{ width: "30px", height: "30px" }} />
                        ) : props?.notification?.tipo === 100 && props.notification.prop_imi === 'f' ? (
                            <img src={PropImiIcon} alt="Icone" style={{ width: "24px", height: "24px", opacity: 0.6 }} />
                        ) : (
                            getIcon(props?.notification?.modulo.toUpperCase(), getCorIcon(props.notification!.tipo))
                        )}
                        </div>


                <div style={{
                    display: 'flex', flexWrap: 'nowrap', fontSize: '12px', width: '100%',
                    fontWeight: (pallet as any).general.weight_600, textTransform: 'uppercase', color: '#1E0736', margin: '5px 10px'
                }}>
                    {props.notification!.tipo === 303 || props.notification!.tipo === 304 ? (
                        <div style={{ width: isMobile ? '175px' : '300px', height: '30px' }}>
                            <span style={{ display: 'block', color: '#1E0736' }}>{props?.notification?.descricao}</span>
                            {isMobile ? (
                                <Grid style={{ display: 'inline-block' }}>
                                    <span style={{ color: '#1E0736', fontWeight: (pallet as any).general.weight_500 }}>
                                        {props?.notification?.data_evento ? formatDate(new Date(props.notification!.data_evento), "dd/MM/yyyy") : "Data indefinida"}
                                    </span>
                                    <span style={{ color: '#00000091', fontWeight: (pallet as any).general.weight_500 }}>
                                        {props?.notification?.criado_em && ` (notificado em ${formatDate(new Date(props.notification!.criado_em), "dd/MM/yyyy")})` || ""}
                                    </span>
                                </Grid>
                            ) : (
                                <p>
                                    <span style={{ color: '#1E0736', fontWeight: (pallet as any).general.weight_500 }}>
                                        {props?.notification?.data_evento ? formatDate(new Date(props.notification!.data_evento), "dd/MM/yyyy") : "Data indefinida"}
                                    </span>
                                    <span style={{ color: '#00000091', fontWeight: (pallet as any).general.weight_500 }}>
                                        {props?.notification?.criado_em && ` (notificado em ${formatDate(new Date(props.notification!.criado_em), "dd/MM/yyyy")})` || ""}
                                    </span>
                                </p>
                            )}
                        </div>
                    ) : (
                        <div onClick={() => checandoProp(Number(tratarId(props.notification!.path)))} style={{ width: isMobile ? '50px' : '350px', height: '30px', cursor: "pointer" }}>
                            <span>{props?.notification?.descricao}</span>
                            {isMobile ? (
                                <>
                                    <span style={{ display: 'inline-block', color: '#1E0736', fontWeight: (pallet as any).general.weight_500 }}>
                                        {props?.notification?.data_evento ? formatDate(new Date(props.notification!.data_evento), "dd/MM/yyyy") : "Data indefinida"}
                                    </span>
                                    <span style={{ color: '#00000091', fontWeight: (pallet as any).general.weight_500 }}>
                                        {props?.notification?.criado_em && ` (notificado em ${formatDate(new Date(props.notification!.criado_em), "dd/MM/yyyy")})` || ""}
                                    </span>
                                </>
                            ) : (
                                <p>
                                    <span style={{ display: 'inline-block', color: '#1E0736', fontWeight: (pallet as any).general.weight_500 }}>
                                        {props?.notification?.data_evento ? formatDate(new Date(props.notification!.data_evento), "dd/MM/yyyy") : "Data indefinida"}
                                    </span>
                                    <span style={{ color: '#00000091', fontWeight: (pallet as any).general.weight_500 }}>
                                        {props?.notification?.criado_em && ` (notificado em ${formatDate(new Date(props.notification!.criado_em), "dd/MM/yyyy")})` || ""}
                                    </span>
                                </p>
                            )}
                        </div>
                    )}

                    {isMobile ? (
                        <div style={{ marginLeft: 'auto' }}>
                            <Grid>
                                {props.notification!.tipo === 100 && props.status === 'A' ? (
                                    <Grid style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                        <div>
                                            <Checkbox
                                                icon={props.notification!.favoritado === 'sim' ? <StarIcon /> : <StarBorderIcon />}
                                                checkedIcon={<StarIcon />}
                                                defaultChecked={props.notification!.favoritado === 'sim'}
                                                value={props.notification!.favoritado === 'sim'}
                                                style={{ color: `${(pallet as any).general.color2}`, height: '3px' }}
                                                onChange={(e) => handleChangeFavorito(e, props.notification!.referencia, 'proposicao')}
                                            />
                                        </div>
                                        {getConfig()?.habMercadosFront
                                            ?
                                            <SeuMercadoWidget
                                                isInteresse={props?.notification?.interesse_mercados}
                                                destaqueText={props?.notification?.mercados_nomes?.map((x, i) => (
                                                    <>
                                                        <div key={x} style={{ fontSize: '10px' }}>
                                                            ✓ {x}
                                                        </div>
                                                        <span style={{ fontStyle: 'italic', color: '#cccccc' }}>
                                                            Trecho: {props.notification!.mercados_trechos[i]}
                                                        </span>
                                                    </>
                                                ))}
                                            />
                                            :
                                            null
                                        }

                                        <ButtonNaoSeguir
                                            id={props.notification!.referencia}
                                            status={props.notification!.seguindo}
                                            onClick={() => seguirClick(props.notification!.referencia, false, props.notification!.path)}
                                            width={isMobile ? "90px" : "140px"}
                                        />

                                        <ButtonSeguir
                                            id={props.notification!.referencia}
                                            status={props.notification!.seguindo}
                                            onClick={() => seguirClick(props.notification!.referencia, true, props.notification!.path)}
                                            width={isMobile ? "90px" : "110px"}
                                        />
                                    </Grid>
                                ) : null}

                                {props.notification!.tipo === 119 && props.status === 'A' ? (
                                    getConfig()?.habMercadosFront ? (
                                        <SeuMercadoWidget
                                            isInteresse={props?.notification?.interesse_mercados}
                                            destaqueText={props?.notification?.mercados_nomes?.map((x, i) => (
                                                <>
                                                    <div key={x} style={{ fontSize: '10px' }}>
                                                        ✓ {x}
                                                    </div>
                                                    <span style={{ fontStyle: 'italic', color: '#cccccc' }}>
                                                        Trecho: {props.notification!.mercados_trechos[i]}
                                                    </span>
                                                </>
                                            ))}
                                        />
                                    ) : null
                                ) : null}

                                <div style={{ marginTop: isMobile && props.notification!.tipo == 117 ? "20px" as any : undefined }} />

                                {props.status === 'A' ? (
                                    <Checkbox
                                        key={props.index}
                                        checked={checked}
                                        onChange={(e) => props.idPermissao !== 3 ? handleChange(e.target.checked, props.index, props.notification!.id) : NewAlert(
                                            "error",
                                            "Não é possível realizar está ação com este perfil.")}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        sx={{ maxHeight: '32px' }}
                                    />
                                ) : null}

                                {props.central === true && props.status === 'ARQ' ? (
                                    <Checkbox
                                        key={props.index}
                                        checked={checked}
                                        onChange={(e) => handleChange(e.target.checked, props.index, props.notification!.id)}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        sx={{ maxHeight: '32px' }}
                                    />
                                ) : null}

                                <NotificacaoMenuButton notificacao={props.notification!} reloadList={props.reloadList} filtro={props.central ? props.filtro : 'A'} />

                                {props.status === 'A' ? (
                                    <IconButton onClick={props.idPermissao !== 3 ? handleArquivar : permissaoErro}>
                                        <InventoryIcon style={{ color: (pallet as any).general.color4 }} />
                                    </IconButton>
                                ) : null}

                                {props.status === 'ARQ' ? (
                                    <IconButton onClick={props.idPermissao !== 3 ? handleDesarquivar : permissaoErro}>
                                        <RestoreFromTrashIcon style={{ width: '26px', height: '26px', color: (pallet as any).general.color4 }} />
                                    </IconButton>
                                ) : null}
                            </Grid>
                        </div>
                    ) : (
                        <div style={{ marginLeft: 'auto' }}>
                            <Grid container item alignItems={'baseline'}>
                                {props.notification!.tipo === 100 && props.status === 'A' ? (
                                    <Checkbox
                                        icon={props.notification!.favoritado === 'sim' ? <StarIcon /> : <StarBorderIcon />}
                                        checkedIcon={<StarIcon />}
                                        defaultChecked={props.notification!.favoritado === 'sim'}
                                        value={props.notification!.favoritado === 'sim'}
                                        style={{ color: `${(pallet as any).general.color2}` }}
                                        onChange={(e) => handleChangeFavorito(e, props.notification!.referencia, 'proposicao')}
                                    />
                                ) : null}

                                {props.notification!.tipo === 100 && props.status === 'A' ? (
                                    getConfig()?.habMercadosFront
                                        ?
                                        <SeuMercadoWidget
                                            isInteresse={props?.notification?.interesse_mercados}
                                            destaqueText={props?.notification?.mercados_nomes?.map((x, i) => (
                                                <>
                                                    <div key={x} style={{ fontSize: '10px' }}>
                                                        ✓ {x}
                                                    </div>
                                                    <span style={{ fontStyle: 'italic', color: '#cccccc' }}>
                                                        Trecho: {props.notification!.mercados_trechos[i]}
                                                    </span>
                                                </>
                                            ))}
                                        />
                                        :
                                        null

                                ) : null}

                                {props.notification!.tipo === 119 && props.status === 'A' ? (
                                    getConfig()?.habMercadosFront ? (
                                        <SeuMercadoWidget
                                            isInteresse={props?.notification?.interesse_mercados}
                                            destaqueText={props?.notification?.mercados_nomes?.map((x, i) => (
                                                <>
                                                    <div key={x} style={{ fontSize: '10px' }}>
                                                        ✓ {x}
                                                    </div>
                                                    <span style={{ fontStyle: 'italic', color: '#cccccc' }}>
                                                        Trecho: {props.notification!.mercados_trechos[i]}
                                                    </span>
                                                </>
                                            ))}
                                        />
                                    ) : null
                                ) : null}

                                {props.notification!.tipo === 100 && props.status === 'A' ? (
                                    <ButtonNaoSeguir
                                        id={props.notification!.referencia}
                                        status={props.notification!.seguindo}
                                        onClick={() => seguirClick(props.notification!.referencia, false, props.notification!.path)}
                                        width={"140px"}
                                    />
                                ) : null}

                                {props.notification!.tipo === 100 && props.status === 'A' ? (
                                    <ButtonSeguir
                                        id={props.notification!.referencia}
                                        status={props.notification!.seguindo}
                                        onClick={() => seguirClick(props.notification!.referencia, true, props.notification!.path)}
                                        width={"80px"}
                                    />
                                ) : null}

                                {props.status === 'A' ? (
                                    <Checkbox
                                        key={props.index}
                                        checked={checked}
                                        onChange={(e) => props.idPermissao !== 3 ? handleChange(e.target.checked, props.index, props.notification!.id) : NewAlert(
                                            "error",
                                            "Não é possível realizar está ação com este perfil.")}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        sx={{ maxHeight: '32px' }}
                                    />
                                ) : null}

                                {props.central === true && props.status === 'ARQ' ? (
                                    <Checkbox
                                        key={props.index}
                                        checked={checked}
                                        onChange={(e) => handleChange(e.target.checked, props.index, props.notification!.id)}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        sx={{ maxHeight: '32px' }}
                                    />
                                ) : null}

                                <NotificacaoMenuButton notificacao={props.notification!} reloadList={props.reloadList} filtro={props.central ? props.filtro : 'A'} />

                                {props.status === 'A' ? (
                                    <IconButton onClick={props.idPermissao !== 3 ? handleArquivar : permissaoErro}>
                                        <InventoryIcon style={{ color: (pallet as any).general.color4 }} />
                                    </IconButton>
                                ) : null}

                                {props.status === 'ARQ' ? (
                                    <IconButton onClick={props.idPermissao !== 3 ? handleDesarquivar : permissaoErro}>
                                        <RestoreFromTrashIcon style={{ width: '26px', height: '26px', color: (pallet as any).general.color4 }} />
                                    </IconButton>
                                ) : null}
                            </Grid>
                        </div>
                    )}
                </div>
            </div>
        {
            props.notification!.tipo === 407 ?

            <>
            <Grid  ref={ref} style={{maxHeight: '300px',textOverflow: "elipsis",overflow: "hidden"}}>
            <NotificacaoNotContent 
                dangerouslySetInnerHTML={{
                    __html: props.notification?.mensagem ? props.notification?.mensagem :
                        "<span>PLP230/2020 • <b>Apensada a</b> PL2267/2019</span>\n" +
                        "<b>Câmara dos Deputados</b>\n" +
                        "<ul>\n" +
                        "<li>\n" +
                        "<b> Ementa:</b> Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.\n" +
                        "</li>\n" +
                        "</ul>",
                }}
            
             />
             </Grid>
             {isOverflowing ? 
             
             <CitacaoModal id={props.notification?.referencia} mensagem={props.notification?.mensagem}/>
            :
            null
            }

             </>
            :
                        <NotificacaoNotContent 
                dangerouslySetInnerHTML={{
                    __html: props.notification?.mensagem ? props.notification?.mensagem :
                        "<span>PLP230/2020 • <b>Apensada a</b> PL2267/2019</span>\n" +
                        "<b>Câmara dos Deputados</b>\n" +
                        "<ul>\n" +
                        "<li>\n" +
                        "<b> Ementa:</b> Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.\n" +
                        "</li>\n" +
                        "</ul>",
                }}
            />


        }
        
            {/* <NotificacaoNotContent
                dangerouslySetInnerHTML={{
                    __html: props.notification?.mensagem ? props.notification?.mensagem :
                        "<span>PLP230/2020 • <b>Apensada a</b> PL2267/2019</span>\n" +
                        "<b>Câmara dos Deputados</b>\n" +
                        "<ul>\n" +
                        "<li>\n" +
                        "<b> Ementa:</b> Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.\n" +
                        "</li>\n" +
                        "</ul>",
                }}
            /> */}

            <NotificacaoNotFooter>
                {props.notification?.links && Array.isArray(props.notification?.links) && JSON.parse(props.notification?.links).map((item: any) => {
                    return <a href={item.url}><LinkIcon style={{ color: '#1E0736', marginRight: '10px' }} /> {item.descricao} </a>
                })}
            </NotificacaoNotFooter>
        </Grid>
    )
};

export default NotificacaoCard;